import { loadStripe } from '@stripe/stripe-js';
import {
  useStripe,
  useElements,
  PaymentElement,
} from '@stripe/react-stripe-js';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { paymentConfig } from '../config';
import { trackPaymentCompleted, trackPaymentInitiated } from '../utils/posthogEvents';

const SESSION_STORAGE_KEY = 'stripe_payment_session';

const savePaymentSession = (sessionData) => {
  const session = {
    ...sessionData,
    timestamp: Date.now(),
  };
  sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
  
  // Update PostHog session properties - ensure PostHog is enabled
  if (window.posthog) {
    try {
      window.posthog.register({
        payment_session_id: sessionData.paymentIntentId,
        payment_amount: sessionData.amount,
        payment_currency: sessionData.currency,
        payment_status: sessionData.status || 'initiated',
      });
      window.posthog.flush();
    } catch (e) {
      console.error('Error registering payment session:', e);
    }
  }
};

const getPaymentSession = () => {
  const data = sessionStorage.getItem(SESSION_STORAGE_KEY);
  return data ? JSON.parse(data) : null;
};

const clearPaymentSession = () => {
  sessionStorage.removeItem(SESSION_STORAGE_KEY);
  if (window.posthog) {
    window.posthog.unregister('payment_session_id');
    window.posthog.unregister('payment_amount');
    window.posthog.unregister('payment_currency');
    window.posthog.unregister('payment_status');
  }
};

export const CheckoutForm = ({
  onSuccess,
  setPaymentData,
  onClose,
  processing,
  setProcessing,
  amount,
  clientSecret,
  currency = 'usd',
  currencySymbol = '$',
  language,
  displayLanguage,
  translations = {
    processing: 'Processing...',
    cancel: 'Cancel',
    pay: 'Pay',
    failedToConfirmPayment: 'Failed to confirm payment',
    errorConfirmingPayment: 'Error confirming payment',
    errorDuringPayment: 'An error occurred during payment',
  },
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [isPaymentComplete, setIsPaymentComplete] = useState(false);
  const [sessionData, setSessionData] = useState(null);

  useEffect(() => {
    const existingSession = getPaymentSession();
    if (existingSession) {
      setSessionData(existingSession);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    setError(null);

    const currentSession = {
      paymentIntentId: clientSecret,
      amount,
      currency,
      startTime: Date.now(),
      status: 'processing',
    };
    savePaymentSession(currentSession);
    setSessionData(currentSession);
    
    // Track payment initiation
    trackPaymentInitiated({
      amount,
      currency,
      paymentIntentId: clientSecret,
    });

    try {
      const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: window.location.origin + '/success' },
        redirect: 'if_required',
      });

      if (stripeError) {
        setError(stripeError.message);
        const failedSession = {
          ...currentSession,
          status: 'failed',
          error: stripeError.message,
          failedAt: Date.now(),
        };
        savePaymentSession(failedSession);
        
        trackPaymentCompleted({
          amount,
          currency,
          status: 'failed',
          error: stripeError.message,
          paymentIntentId: paymentIntent?.id,
        });
      } else {
        const paymentIntentId = paymentIntent.id;
        try {
          const response = await axios.post('/billing/confirm-payment', {
            paymentIntentId,
            language,
            displayLanguage,
          });
          if (response.data.status === 200) {
            const completedSession = {
              ...currentSession,
              status: 'completed',
              completedAt: Date.now(),
              email: response.data.response?.email,
              subscriptionId: response.data.response?.subscriptionId,
            };
            savePaymentSession(completedSession);
            
            trackPaymentCompleted({
              amount,
              currency,
              status: 'completed',
              paymentIntentId,
              paymentMethod: paymentIntent.payment_method,
              paymentStatus: paymentIntent.status,
              email: response.data.response?.email,
              domain: response.data.response?.domain,
              firstName: response.data.response?.firstName,
              lastName: response.data.response?.lastName,
              planType: response.data.response?.planType,
              subscriptionId: response.data.response?.subscriptionId,
              nextPaymentDate: response.data.response?.nextPaymentDate,
            });
            
            setTimeout(() => clearPaymentSession(), 2000);
            onSuccess();
            setPaymentData(response?.data);
          } else {
            setError(response.data.message || translations.failedToConfirmPayment);
            const failedSession = {
              ...currentSession,
              status: 'confirmation_failed',
              error: response.data.message,
              failedAt: Date.now(),
            };
            savePaymentSession(failedSession);
            
            trackPaymentCompleted({
              amount,
              currency,
              status: 'confirmation_failed',
              error: response.data.message,
              paymentIntentId,
            });
          }
        } catch (confirmError) {
          setError(
            confirmError.response?.data?.message ||
              translations.errorConfirmingPayment,
          );
          const errorSession = {
            ...currentSession,
            status: 'error',
            error: confirmError.response?.data?.message || confirmError.message,
            failedAt: Date.now(),
          };
          savePaymentSession(errorSession);
          
          trackPaymentCompleted({
            amount,
            currency,
            status: 'error',
            error: confirmError.response?.data?.message || confirmError.message,
            paymentIntentId,
          });
        }
      }
    } catch (err) {
      setError(err.message || translations.errorDuringPayment);
      const errorSession = {
        ...currentSession,
        status: 'error',
        error: err.message,
        failedAt: Date.now(),
      };
      savePaymentSession(errorSession);
      
      trackPaymentCompleted({
        amount,
        currency,
        status: 'error',
        error: err.message,
      });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement
        options={{
          wallets: {
            applePay: 'auto',
            googlePay: 'auto',
          },
          appearance: {
            theme: 'flat',
            variables: {
              colorPrimary: '#000',
            },
            rules: {
              '.Input': {
                boxShadow: 'none',
                border: '1px solid #ccc',
              },
            },
          },
        }}
        onChange={(event) => {
          setIsPaymentComplete(event.complete);
        }}
      />

      {error && <p className="alert alert-danger mt-3">{error}</p>}
      <div className="d-flex gap-2 mt-3">
        <button
          type="button"
          className="btn btn-outline-secondary flex-grow-1"
          onClick={onClose}
          disabled={processing}
        >
          {translations.cancel}
        </button>
        <button
          type="submit"
          className="btn btn-primary flex-grow-1"
          disabled={!stripe || processing || !isPaymentComplete}
        >
          {processing
            ? translations.processing
            : `${translations.pay} ${currencySymbol}${amount}`}
        </button>
      </div>
    </form>
  );
};

if (!paymentConfig.stripe.publishableKey) {
  console.error('Stripe publishable key is missing!');
}

export const stripePromise = loadStripe(paymentConfig.stripe.publishableKey);
